import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Checkbox } from '../ui/checkbox';
import { 
  Plus, Pencil, Trash, User, Mail, Flag, Calendar, MapPin,
  Award, Grid, List, Search, Upload, X, FileText, Eye, 
  GraduationCap, Shield, ChevronRight, Download, FileUp, AlertCircle, CheckCircle,
  Phone, Home, Building, Globe, AlertTriangle
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import { CERTIFICATION_DATA } from './FreedivingLevelsTab';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const initialFreediverForm = {
  first_name: '', last_name: '', email: '', nationality: '',
  gender: '', date_of_birth: '', place_of_birth: '', photo_url: '',
  certification_agency: '', certification_level: '', experience_level: '',
  is_coach: false,
  // Address fields
  address_number: '', address_type: '', address_name: '',
  city: '', state_province: '', postal_code: '', country: '',
  phone_number: '',
  // Medical certificate fields
  medical_certificate_valid: false,
  medical_certificate_url: '',
  medical_certificate_upload_date: null,
  medical_certificate_expiry_date: null
};

// Address type options
const ADDRESS_TYPES = [
  'Street', 'Road', 'Avenue', 'Boulevard', 'Line', 'Drive', 'Lane', 
  'Court', 'Place', 'Way', 'Circle', 'Crescent', 'Highway', 'Route', 'Other'
];

const FreediversTab = ({ t, language, freedivers, onRefresh, onViewPerformances }) => {
  const [viewMode, setViewMode] = useState('list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedFreediver, setSelectedFreediver] = useState(null);
  const [freediverForm, setFreediverForm] = useState(initialFreediverForm);
  const [searchQuery, setSearchQuery] = useState('');
  const [nationalityFilter, setNationalityFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [photoPreview, setPhotoPreview] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, freediver: null });
  const [deleting, setDeleting] = useState(false);
  const photoInputRef = useRef(null);
  const certificateInputRef = useRef(null);
  const csvInputRef = useRef(null);

  // Get unique nationalities for filter
  const uniqueNationalities = [...new Set(freedivers.map(f => f.nationality).filter(Boolean))].sort();

  // Filter freedivers based on search and filters
  const filteredFreedivers = freedivers.filter(freediver => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const fullName = `${freediver.first_name} ${freediver.last_name}`.toLowerCase();
      const email = freediver.email?.toLowerCase() || '';
      if (!fullName.includes(query) && !email.includes(query)) {
        return false;
      }
    }
    // Nationality filter
    if (nationalityFilter !== 'all' && freediver.nationality !== nationalityFilter) {
      return false;
    }
    // Gender filter
    if (genderFilter !== 'all' && freediver.gender !== genderFilter) {
      return false;
    }
    return true;
  });

  // Sort logic
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedFreedivers = [...filteredFreedivers].sort((a, b) => {
    if (!sortColumn) return 0;
    const dir = sortDirection === 'asc' ? 1 : -1;
    switch (sortColumn) {
      case 'name': {
        const nameA = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
        const nameB = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
        return nameA.localeCompare(nameB) * dir;
      }
      case 'email': {
        return (a.email || '').localeCompare(b.email || '') * dir;
      }
      case 'nationality': {
        return (a.nationality || '').localeCompare(b.nationality || '') * dir;
      }
      case 'gender': {
        return (a.gender || '').localeCompare(b.gender || '') * dir;
      }
      case 'age': {
        const getAge = (dob) => {
          if (!dob) return 0;
          const birth = new Date(dob);
          const today = new Date();
          let age = today.getFullYear() - birth.getFullYear();
          const m = today.getMonth() - birth.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
          return age;
        };
        return (getAge(a.date_of_birth) - getAge(b.date_of_birth)) * dir;
      }
      default:
        return 0;
    }
  });

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <span className="ml-1 opacity-30">{'\u2195'}</span>;
    return <span className="ml-1 text-teal-400">{sortDirection === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };

  const handleCreateFreediver = async (e) => {
    e.preventDefault();
    try {
      const freediverData = {
        ...freediverForm,
        date_of_birth: new Date(freediverForm.date_of_birth).toISOString()
      };

      await axios.post(`${API_BASE_URL}/api/freedivers`, freediverData);
      setShowCreateModal(false);
      setFreediverForm(initialFreediverForm);
      setPhotoPreview(null);
      setCertificatePreview(null);
      onRefresh();
    } catch (error) {
      console.error('Error creating freediver:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail);
      } else {
        alert('Error registering freediver. Please try again.');
      }
    }
  };

  const handleUpdateFreediver = async (e) => {
    e.preventDefault();
    try {
      const freediverData = {
        ...freediverForm,
        date_of_birth: new Date(freediverForm.date_of_birth).toISOString()
      };

      await axios.put(`${API_BASE_URL}/api/freedivers/${selectedFreediver.id}`, freediverData);
      setShowEditModal(false);
      setSelectedFreediver(null);
      setFreediverForm(initialFreediverForm);
      setPhotoPreview(null);
      setCertificatePreview(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating freediver:', error);
      alert('Error updating freediver. Please try again.');
    }
  };

  const handleDeleteFreediver = async () => {
    if (!deleteConfirm.freediver) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE_URL}/api/freedivers/${deleteConfirm.freediver.id}`);
      setDeleteConfirm({ show: false, freediver: null });
      onRefresh();
    } catch (error) {
      console.error('Error deleting freediver:', error);
      const errorMessage = error.response?.data?.detail || t('errorDeletingFreediver') || 'Error deleting freediver. Please try again.';
      alert(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirm = (freediver) => {
    setDeleteConfirm({ show: true, freediver });
  };

  const openEditModal = (freediver) => {
    setSelectedFreediver(freediver);
    setFreediverForm({
      first_name: freediver.first_name,
      last_name: freediver.last_name,
      email: freediver.email,
      nationality: freediver.nationality,
      gender: freediver.gender,
      date_of_birth: freediver.date_of_birth?.split('T')[0] || '',
      place_of_birth: freediver.place_of_birth || '',
      photo_url: freediver.photo_url || '',
      certification_agency: freediver.certification_agency || '',
      certification_level: freediver.certification_level || '',
      experience_level: freediver.experience_level || '',
      is_coach: freediver.is_coach || false,
      // Address fields
      address_number: freediver.address_number || '',
      address_type: freediver.address_type || '',
      address_name: freediver.address_name || '',
      city: freediver.city || '',
      state_province: freediver.state_province || '',
      postal_code: freediver.postal_code || '',
      country: freediver.country || '',
      phone_number: freediver.phone_number || '',
      // Medical certificate fields
      medical_certificate_valid: freediver.medical_certificate_valid || false,
      medical_certificate_url: freediver.medical_certificate_url || '',
      medical_certificate_upload_date: freediver.medical_certificate_upload_date?.split('T')[0] || null,
      medical_certificate_expiry_date: freediver.medical_certificate_expiry_date?.split('T')[0] || null
    });
    setPhotoPreview(freediver.photo_url || null);
    setCertificatePreview(freediver.medical_certificate_url ? 'exists' : null);
    setShowPdfPreview(false);
    setShowEditModal(true);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo file must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFreediverForm({...freediverForm, photo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setPhotoPreview(null);
    setFreediverForm({...freediverForm, photo_url: ''});
  };

  const handleCertificateUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Certificate file must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const today = new Date();
        const expiryDate = new Date(today);
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        
        setCertificatePreview('uploaded');
        setFreediverForm({
          ...freediverForm, 
          medical_certificate_url: reader.result,
          medical_certificate_valid: true,
          medical_certificate_upload_date: today.toISOString().split('T')[0],
          medical_certificate_expiry_date: expiryDate.toISOString().split('T')[0]
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCertificate = () => {
    setCertificatePreview(null);
    setShowPdfPreview(false);
    setFreediverForm({
      ...freediverForm, 
      medical_certificate_url: '',
      medical_certificate_valid: false,
      medical_certificate_upload_date: null,
      medical_certificate_expiry_date: null
    });
  };

  // CSV Import Functions
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/freedivers/template/csv`);
      const { template, instructions } = response.data;
      
      // Create example row
      const exampleRow = "John,Doe,john.doe@example.com,USA,male,1990-01-15,New York,AIDA,Level 2,intermediate,false";
      const csvContent = template + "\n" + exampleRow;
      
      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'freedivers_template.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template');
    }
  };

  const handleCsvFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedCsvFile(file);
    }
  };

  const handleImportCsv = async () => {
    if (!selectedCsvFile) {
      alert(t('selectCSVFile'));
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedCsvFile);

      const response = await axios.post(`${API_BASE_URL}/api/freedivers/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResults(response.data);
      if (response.data.total_created > 0) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      setImportResults({
        total_processed: 0,
        total_created: 0,
        total_errors: 1,
        errors: [{ row: 0, error: error.response?.data?.detail || 'Unknown error' }]
      });
    } finally {
      setImporting(false);
    }
  };

  const resetImportModal = () => {
    setShowImportModal(false);
    setSelectedCsvFile(null);
    setImportResults(null);
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const birth = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const renderFreediverForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Photo Upload */}
      <div className="flex items-center gap-4 pb-4 border-b border-blue-700/30">
        {photoPreview ? (
          <div className="relative">
            <img src={photoPreview} alt="Preview" className="w-24 h-24 object-cover rounded-full border-2 border-blue-500" />
            <button
              type="button"
              onClick={removePhoto}
              className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 border-2 border-dashed border-blue-600 rounded-full flex items-center justify-center text-blue-400">
            <User size={32} />
          </div>
        )}
        <div>
          <input
            type="file"
            ref={photoInputRef}
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => photoInputRef.current?.click()}
            className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
          >
            <Upload className="mr-2" size={16} />
            {t('uploadPhoto')}
          </Button>
          <p className="text-xs text-blue-400 mt-1">Max 5MB, JPG/PNG</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('firstName')}</Label>
          <Input
            value={freediverForm.first_name}
            onChange={(e) => setFreediverForm({...freediverForm, first_name: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            required
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('lastName')}</Label>
          <Input
            value={freediverForm.last_name}
            onChange={(e) => setFreediverForm({...freediverForm, last_name: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            required
          />
        </div>
      </div>

      <div>
        <Label className="text-blue-200">{t('email')}</Label>
        <Input
          type="email"
          value={freediverForm.email}
          onChange={(e) => setFreediverForm({...freediverForm, email: e.target.value})}
          className="bg-blue-800/50 border-blue-600 text-white"
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('nationality')}</Label>
          <Input
            value={freediverForm.nationality}
            onChange={(e) => setFreediverForm({...freediverForm, nationality: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="e.g., USA"
            required
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('gender')}</Label>
          <Select value={freediverForm.gender} onValueChange={(value) => setFreediverForm({...freediverForm, gender: value})}>
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
              <SelectValue placeholder={t('selectGender')} />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600">
              <SelectItem value="male">{t('male')}</SelectItem>
              <SelectItem value="female">{t('female')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('dateOfBirth')}</Label>
          <Input
            type="date"
            value={freediverForm.date_of_birth}
            onChange={(e) => setFreediverForm({...freediverForm, date_of_birth: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            required
          />
        </div>
        <div>
          <Label className="text-blue-200">{t('placeOfBirth')}</Label>
          <Input
            value={freediverForm.place_of_birth}
            onChange={(e) => setFreediverForm({...freediverForm, place_of_birth: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white"
            placeholder="e.g., Paris, France"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-blue-200">{t('certificationAgency')}</Label>
          <Select 
            value={freediverForm.certification_agency} 
            onValueChange={(value) => {
              // Reset certification level when agency changes
              setFreediverForm({
                ...freediverForm, 
                certification_agency: value,
                certification_level: ''
              });
            }}
          >
            <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
              <SelectValue placeholder={t('selectAgency')} />
            </SelectTrigger>
            <SelectContent className="bg-blue-800 border-blue-600 max-h-60">
              {Object.keys(CERTIFICATION_DATA).map((agencyKey) => (
                <SelectItem key={agencyKey} value={agencyKey}>
                  {CERTIFICATION_DATA[agencyKey].name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-blue-200">{t('certificationLevel')}</Label>
          {freediverForm.certification_agency && CERTIFICATION_DATA[freediverForm.certification_agency] ? (
            <Select 
              value={freediverForm.certification_level} 
              onValueChange={(value) => setFreediverForm({...freediverForm, certification_level: value})}
            >
              <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                <SelectValue placeholder={t('selectCertificationLevel')} />
              </SelectTrigger>
              <SelectContent className="bg-blue-800 border-blue-600 max-h-60">
                {CERTIFICATION_DATA[freediverForm.certification_agency].levels.map((level) => (
                  <SelectItem key={level.code} value={level.name}>
                    {level.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-10 px-3 py-2 rounded-md border bg-blue-800/30 border-blue-700/50 text-blue-400 text-sm flex items-center">
              {t('noCertificationAgency')}
            </div>
          )}
        </div>
      </div>

      <div>
        <Label className="text-blue-200">{t('experienceLevel')}</Label>
        <Select 
          value={freediverForm.experience_level} 
          onValueChange={(value) => setFreediverForm({...freediverForm, experience_level: value})}
        >
          <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
            <SelectValue placeholder={t('selectExperienceLevel')} />
          </SelectTrigger>
          <SelectContent className="bg-blue-800 border-blue-600">
            <SelectItem value="beginner">{t('beginner')}</SelectItem>
            <SelectItem value="intermediate">{t('intermediate')}</SelectItem>
            <SelectItem value="advanced">{t('advanced')}</SelectItem>
            <SelectItem value="expert">{t('expert')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Medical Certificate Section */}
      <div className="border border-blue-700/50 rounded-lg p-4 bg-blue-900/30">
        <h4 className="text-blue-200 font-semibold mb-3 flex items-center">
          <Shield className="mr-2" size={18} />
          {t('medicalCertificate')}
        </h4>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="certificate_valid"
              checked={freediverForm.medical_certificate_valid}
              onCheckedChange={(checked) => setFreediverForm({...freediverForm, medical_certificate_valid: checked})}
            />
            <Label htmlFor="certificate_valid" className="text-blue-300">
              {t('certificateValid')}
            </Label>
          </div>
        </div>

        {/* Certificate Validity Dates - Editable */}
        {freediverForm.medical_certificate_valid && (
          <div className="mt-3 p-3 bg-blue-800/40 rounded-lg border border-blue-600/30">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-blue-400 text-xs">{t('certificateStartDate')}</Label>
                <Input
                  type="date"
                  value={freediverForm.medical_certificate_upload_date || ''}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    // Auto-calculate expiry date as 1 year from start
                    let expiryDate = freediverForm.medical_certificate_expiry_date;
                    if (startDate && !freediverForm.medical_certificate_expiry_date) {
                      const d = new Date(startDate);
                      d.setFullYear(d.getFullYear() + 1);
                      expiryDate = d.toISOString().split('T')[0];
                    }
                    setFreediverForm({
                      ...freediverForm, 
                      medical_certificate_upload_date: startDate,
                      medical_certificate_expiry_date: expiryDate
                    });
                  }}
                  className="bg-blue-800/50 border-blue-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-blue-400 text-xs">{t('certificateExpiryDate')}</Label>
                <Input
                  type="date"
                  value={freediverForm.medical_certificate_expiry_date || ''}
                  onChange={(e) => setFreediverForm({...freediverForm, medical_certificate_expiry_date: e.target.value})}
                  className="bg-blue-800/50 border-blue-600 text-white mt-1"
                />
              </div>
            </div>
            {freediverForm.medical_certificate_expiry_date && (
              <div className="mt-2 flex items-center gap-2">
                {(() => {
                  const expiry = new Date(freediverForm.medical_certificate_expiry_date);
                  const daysLeft = Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24));
                  return daysLeft <= 0 
                    ? <Badge className="bg-red-600 text-white">{t('expired')}</Badge>
                    : daysLeft <= 30 
                      ? <Badge className="bg-orange-600 text-white">{daysLeft} {t('daysLeft')}</Badge>
                      : <Badge className="bg-green-600 text-white">{t('valid')}</Badge>;
                })()}
              </div>
            )}
          </div>
        )}

        {/* Certificate Upload and Preview */}
        <div className="mt-3 space-y-3">
          {certificatePreview ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 bg-green-900/30 px-3 py-2 rounded border border-green-600/50">
                <FileText className="text-green-400" size={18} />
                <span className="text-green-300 text-sm">{t('certificateUploaded')}</span>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPdfPreview(!showPdfPreview)}
                  className="text-blue-300 hover:text-white hover:bg-blue-700/50 h-7 px-2"
                >
                  <Eye size={14} className="mr-1" />
                  {t('previewPDF')}
                </Button>
                <button
                  type="button"
                  onClick={removeCertificate}
                  className="text-red-400 hover:text-red-300"
                >
                  <X size={16} />
                </button>
              </div>
              
              {/* PDF Preview Iframe */}
              {showPdfPreview && freediverForm.medical_certificate_url && (
                <div className="border border-blue-600/50 rounded-lg overflow-hidden bg-gray-900">
                  <div className="bg-blue-800/50 px-3 py-2 flex items-center justify-between border-b border-blue-600/30">
                    <span className="text-blue-200 text-sm font-medium">{t('previewCertificate')}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPdfPreview(false)}
                      className="h-6 w-6 p-0 text-blue-300 hover:text-white"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                  {freediverForm.medical_certificate_url.startsWith('data:application/pdf') ? (
                    <iframe
                      src={freediverForm.medical_certificate_url}
                      className="w-full h-80"
                      title="Certificate Preview"
                    />
                  ) : freediverForm.medical_certificate_url.startsWith('data:image') ? (
                    <img 
                      src={freediverForm.medical_certificate_url} 
                      alt="Certificate"
                      className="w-full max-h-80 object-contain"
                    />
                  ) : (
                    <div className="p-4 text-center text-blue-300">
                      <FileText size={48} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">{t('previewCertificate')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <>
              <input
                type="file"
                ref={certificateInputRef}
                accept=".pdf,image/*"
                onChange={handleCertificateUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => certificateInputRef.current?.click()}
                className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
                size="sm"
              >
                <Upload className="mr-2" size={14} />
                {t('uploadCertificate')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Address Information Section */}
      <div className="border-t border-blue-700/50 pt-4 mt-4">
        <h4 className="text-blue-200 font-semibold mb-3 flex items-center">
          <Home className="mr-2" size={18} />
          {t('addressInformation') || 'Address Information'}
        </h4>
        
        {/* Phone Number */}
        <div className="mb-4">
          <Label className="text-blue-300">
            <Phone className="inline mr-2" size={14} />
            {t('phoneNumber') || 'Phone Number'}
          </Label>
          <Input
            type="tel"
            placeholder="+1 234 567 8900"
            value={freediverForm.phone_number}
            onChange={(e) => setFreediverForm({...freediverForm, phone_number: e.target.value})}
            className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
          />
        </div>
        
        {/* Address Line 1: Number, Type, Name */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div>
            <Label className="text-blue-300">{t('addressNumber') || 'Number'}</Label>
            <Input
              type="text"
              placeholder="123"
              value={freediverForm.address_number}
              onChange={(e) => setFreediverForm({...freediverForm, address_number: e.target.value})}
              className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
            />
          </div>
          <div>
            <Label className="text-blue-300">{t('addressType') || 'Type'}</Label>
            <Select
              value={freediverForm.address_type}
              onValueChange={(value) => setFreediverForm({...freediverForm, address_type: value})}
            >
              <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                <SelectValue placeholder={t('selectType') || 'Select type'} />
              </SelectTrigger>
              <SelectContent className="bg-blue-900 border-blue-700">
                {ADDRESS_TYPES.map(type => (
                  <SelectItem key={type} value={type} className="text-white hover:bg-blue-700">
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-blue-300">{t('addressName') || 'Street Name'}</Label>
            <Input
              type="text"
              placeholder="Main Street"
              value={freediverForm.address_name}
              onChange={(e) => setFreediverForm({...freediverForm, address_name: e.target.value})}
              className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
            />
          </div>
        </div>
        
        {/* Address Line 2: City, State/Province */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <Label className="text-blue-300">
              <Building className="inline mr-2" size={14} />
              {t('city') || 'City'}
            </Label>
            <Input
              type="text"
              placeholder="Montreal"
              value={freediverForm.city}
              onChange={(e) => setFreediverForm({...freediverForm, city: e.target.value})}
              className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
            />
          </div>
          <div>
            <Label className="text-blue-300">{t('stateProvince') || 'State/Province'}</Label>
            <Input
              type="text"
              placeholder="Quebec"
              value={freediverForm.state_province}
              onChange={(e) => setFreediverForm({...freediverForm, state_province: e.target.value})}
              className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
            />
          </div>
        </div>
        
        {/* Address Line 3: Postal Code, Country */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-blue-300">{t('postalCode') || 'Postal Code'}</Label>
            <Input
              type="text"
              placeholder="H2X 1Y4"
              value={freediverForm.postal_code}
              onChange={(e) => setFreediverForm({...freediverForm, postal_code: e.target.value})}
              className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
            />
          </div>
          <div>
            <Label className="text-blue-300">
              <Globe className="inline mr-2" size={14} />
              {t('addressCountry') || 'Country'}
            </Label>
            <Input
              type="text"
              placeholder="Canada"
              value={freediverForm.country}
              onChange={(e) => setFreediverForm({...freediverForm, country: e.target.value})}
              className="bg-blue-800/50 border-blue-600 text-white placeholder-blue-400"
            />
          </div>
        </div>
      </div>

      {/* Coach Checkbox */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_coach"
          checked={freediverForm.is_coach}
          onCheckedChange={(checked) => setFreediverForm({...freediverForm, is_coach: checked})}
        />
        <Label htmlFor="is_coach" className="text-blue-300">
          <GraduationCap className="inline mr-2" size={16} />
          {t('markAsCoach')}
        </Label>
      </div>

      <DialogFooter>
        <Button type="submit" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200">{t('freedivers')}</h2>
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-blue-800/30 rounded-lg p-1">
            <Button
              onClick={() => setViewMode('grid')}
              size="sm"
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              className={viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
            >
              <Grid size={16} />
            </Button>
            <Button
              onClick={() => setViewMode('list')}
              size="sm"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              className={viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-blue-300 hover:text-white'}
            >
              <List size={16} />
            </Button>
          </div>

          {/* CSV Import Buttons */}
          <Button
            onClick={handleDownloadTemplate}
            variant="outline"
            size="sm"
            className="border-teal-500 text-teal-300 hover:bg-teal-700/50"
            data-testid="download-template-btn"
          >
            <Download className="mr-2" size={16} />
            {t('downloadTemplate')}
          </Button>
          
          <Button
            onClick={() => setShowImportModal(true)}
            variant="outline"
            size="sm"
            className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
            data-testid="import-csv-btn"
          >
            <FileUp className="mr-2" size={16} />
            {t('importCSV')}
          </Button>

          {/* Create Freediver Dialog */}
          <Dialog open={showCreateModal} onOpenChange={(open) => { 
            if (open) { setFreediverForm({...initialFreediverForm}); setPhotoPreview(null); }
            setShowCreateModal(open); 
            if (!open) onRefresh(); 
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white border-none">
                <Plus className="mr-2" size={18} />
                {t('addFreediver')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-blue-200">{t('addFreediver')}</DialogTitle>
                <DialogDescription className="text-blue-300">
                  {t('addFreediverDescription')}
                </DialogDescription>
              </DialogHeader>
              {renderFreediverForm(handleCreateFreediver, t('register'))}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="bg-gradient-to-r from-blue-800/30 to-teal-800/30 border-blue-700/20">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" size={18} />
                <Input
                  placeholder={t('searchFreedivers')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-blue-800/50 border-blue-600 text-white placeholder:text-blue-400"
                />
              </div>
            </div>
            
            {/* Nationality Filter */}
            <Select value={nationalityFilter} onValueChange={setNationalityFilter}>
              <SelectTrigger className="w-[150px] bg-blue-800/50 border-blue-600 text-white">
                <SelectValue placeholder={t('nationality')} />
              </SelectTrigger>
              <SelectContent className="bg-blue-800 border-blue-600 max-h-60">
                <SelectItem value="all">{t('allNationalities')}</SelectItem>
                {uniqueNationalities.map(nat => (
                  <SelectItem key={nat} value={nat}>{nat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Gender Filter */}
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-[120px] bg-blue-800/50 border-blue-600 text-white">
                <SelectValue placeholder={t('gender')} />
              </SelectTrigger>
              <SelectContent className="bg-blue-800 border-blue-600">
                <SelectItem value="all">{t('all')}</SelectItem>
                <SelectItem value="male">{t('male')}</SelectItem>
                <SelectItem value="female">{t('female')}</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Results count */}
            <Badge variant="secondary" className="bg-blue-700/50 text-blue-200">
              {sortedFreedivers.length} {t('freedivers').toLowerCase()}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Freedivers Display */}
      {sortedFreedivers.length === 0 ? (
        <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm">
          <CardContent className="text-center py-12">
            <User size={48} className="mx-auto text-blue-400 mb-4" />
            <h3 className="text-xl font-semibold text-blue-200 mb-2">{t('noFreediversFound')}</h3>
            <p className="text-blue-300 mb-4">{t('adjustFiltersOrRegister')}</p>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sortedFreedivers.map((freediver) => (
            <Card 
              key={freediver.id} 
              className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm hover:scale-[1.02] transition-transform duration-200"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  {freediver.photo_url ? (
                    <img src={freediver.photo_url} alt="" className="w-14 h-14 object-cover rounded-full border-2 border-blue-500" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-teal-600 flex items-center justify-center text-white text-lg font-bold">
                      {freediver.first_name?.[0]}{freediver.last_name?.[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-blue-200 text-base truncate">
                      {freediver.first_name} {freediver.last_name}
                    </CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="border-teal-500 text-teal-300 text-xs">
                        {freediver.nationality}
                      </Badge>
                      {freediver.is_coach && (
                        <Badge className="bg-purple-600 text-white text-xs">
                          <GraduationCap size={10} className="mr-1" />
                          Coach
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-1 text-sm text-blue-300 mb-3">
                  <p className="flex items-center">
                    <Mail className="mr-2" size={12} />
                    <span className="truncate">{freediver.email}</span>
                  </p>
                  <p className="flex items-center">
                    <User className="mr-2" size={12} />
                    {freediver.gender === 'male' ? t('male') : t('female')} • {calculateAge(freediver.date_of_birth)} {t('yearsOld')}
                  </p>
                  {freediver.medical_certificate_valid && (
                    <p className="flex items-center text-green-400">
                      <Shield className="mr-2" size={12} />
                      {t('certificateValid')}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => onViewPerformances(freediver)}
                    size="sm"
                    variant="outline"
                    className="flex-1 border-teal-500 text-teal-300 hover:bg-teal-700/50"
                  >
                    <Eye size={14} className="mr-1" />
                    {t('viewPerformances')}
                  </Button>
                  <Button
                    onClick={() => openEditModal(freediver)}
                    size="sm"
                    variant="outline"
                    className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
                  >
                    <Pencil size={14} />
                  </Button>
                  <Button
                    onClick={() => openDeleteConfirm(freediver)}
                    size="sm"
                    variant="outline"
                    className="border-red-500 text-red-300 hover:bg-red-700/50"
                    data-testid={`delete-freediver-${freediver.id}`}
                  >
                    <Trash size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="space-y-2">
          {/* List Header - Clickable for sorting */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-blue-800/30 rounded-lg text-xs font-semibold text-blue-300 uppercase">
            <div className="col-span-3 cursor-pointer select-none hover:text-teal-300 transition-colors" onClick={() => handleSort('name')} data-testid="sort-name">
              {t('name')}<SortIcon column="name" />
            </div>
            <div className="col-span-2 cursor-pointer select-none hover:text-teal-300 transition-colors" onClick={() => handleSort('email')} data-testid="sort-email">
              {t('email')}<SortIcon column="email" />
            </div>
            <div className="col-span-1 cursor-pointer select-none hover:text-teal-300 transition-colors" onClick={() => handleSort('nationality')} data-testid="sort-nationality">
              {t('nationality')}<SortIcon column="nationality" />
            </div>
            <div className="col-span-1 cursor-pointer select-none hover:text-teal-300 transition-colors" onClick={() => handleSort('gender')} data-testid="sort-gender">
              {t('gender')}<SortIcon column="gender" />
            </div>
            <div className="col-span-1 cursor-pointer select-none hover:text-teal-300 transition-colors" onClick={() => handleSort('age')} data-testid="sort-age">
              {t('age')}<SortIcon column="age" />
            </div>
            <div className="col-span-1">{t('certification')}</div>
            <div className="col-span-1">{t('medical')}</div>
            <div className="col-span-2 text-right">{t('actions')}</div>
          </div>
          
          {sortedFreedivers.map((freediver) => (
            <div 
              key={freediver.id}
              className="grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-blue-800/30 to-teal-800/30 rounded-lg border border-blue-700/30 items-center hover:bg-blue-700/30 transition-colors"
            >
              <div className="col-span-3 flex items-center gap-2">
                {freediver.photo_url ? (
                  <img src={freediver.photo_url} alt="" className="w-8 h-8 object-cover rounded-full border border-blue-500" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
                    {freediver.first_name?.[0]}{freediver.last_name?.[0]}
                  </div>
                )}
                <span className="text-blue-200 font-medium truncate">
                  {freediver.first_name} {freediver.last_name}
                </span>
                {freediver.is_coach && (
                  <Badge className="bg-purple-600 text-white text-xs ml-1">Coach</Badge>
                )}
              </div>
              <div className="col-span-2 text-blue-300 text-sm truncate">
                {freediver.email}
              </div>
              <div className="col-span-1">
                <Badge variant="outline" className="border-teal-500 text-teal-300 text-xs">
                  {freediver.nationality}
                </Badge>
              </div>
              <div className="col-span-1 text-blue-300 text-sm">
                {freediver.gender === 'male' ? '♂' : '♀'} {freediver.gender === 'male' ? t('male') : t('female')}
              </div>
              <div className="col-span-1 text-blue-300 text-sm">
                {calculateAge(freediver.date_of_birth)}
              </div>
              <div className="col-span-1 text-blue-400 text-xs">
                {freediver.certification_agency || '-'}
              </div>
              <div className="col-span-1">
                {freediver.medical_certificate_valid ? (
                  <Badge className="bg-green-600/50 text-green-200 text-xs">
                    <Shield size={10} className="mr-1" />
                    Valid
                  </Badge>
                ) : (
                  <span className="text-red-400 text-xs">-</span>
                )}
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button
                  onClick={() => onViewPerformances(freediver)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-teal-300 hover:text-white hover:bg-teal-700/50"
                  title={t('viewPerformances')}
                >
                  <ChevronRight size={16} />
                </Button>
                <Button
                  onClick={() => openEditModal(freediver)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-blue-300 hover:text-white hover:bg-blue-700/50"
                >
                  <Pencil size={14} />
                </Button>
                <Button
                  onClick={() => openDeleteConfirm(freediver)}
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2 text-red-300 hover:text-white hover:bg-red-700/50"
                  data-testid={`delete-freediver-list-${freediver.id}`}
                >
                  <Trash size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CSV Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(open) => !open && resetImportModal()}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-blue-200 flex items-center">
              <FileUp className="mr-2" size={18} />
              {t('importFreedivers')}
            </DialogTitle>
            <DialogDescription className="text-blue-300">
              {t('selectCSVFile')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Input */}
            <div className="space-y-2">
              <input
                type="file"
                ref={csvInputRef}
                accept=".csv"
                onChange={handleCsvFileSelect}
                className="hidden"
              />
              <div 
                onClick={() => csvInputRef.current?.click()}
                className="border-2 border-dashed border-blue-600 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-800/30 transition-colors"
              >
                {selectedCsvFile ? (
                  <div className="flex items-center justify-center gap-2 text-green-300">
                    <CheckCircle size={20} />
                    <span>{selectedCsvFile.name}</span>
                  </div>
                ) : (
                  <div className="text-blue-300">
                    <FileUp size={32} className="mx-auto mb-2 opacity-50" />
                    <p>{t('selectCSVFile')}</p>
                    <p className="text-xs text-blue-400 mt-1">CSV files only</p>
                  </div>
                )}
              </div>
            </div>

            {/* Import Results */}
            {importResults && (
              <div className="space-y-3 p-4 bg-blue-800/30 rounded-lg border border-blue-700/50">
                <h4 className="font-semibold text-blue-200">{t('csvImportResults')}</h4>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-blue-700/30 rounded">
                    <div className="text-2xl font-bold text-blue-200">{importResults.total_processed}</div>
                    <div className="text-xs text-blue-400">{t('totalProcessed')}</div>
                  </div>
                  <div className="p-2 bg-green-700/30 rounded">
                    <div className="text-2xl font-bold text-green-300">{importResults.total_created}</div>
                    <div className="text-xs text-green-400">{t('totalCreated')}</div>
                  </div>
                  <div className="p-2 bg-red-700/30 rounded">
                    <div className="text-2xl font-bold text-red-300">{importResults.total_errors}</div>
                    <div className="text-xs text-red-400">{t('totalErrors')}</div>
                  </div>
                </div>

                {/* Error Details */}
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
                    <h5 className="text-sm font-medium text-red-300 flex items-center">
                      <AlertCircle size={14} className="mr-1" />
                      {t('totalErrors')}:
                    </h5>
                    {importResults.errors.map((error, idx) => (
                      <div key={idx} className="text-xs p-2 bg-red-900/30 rounded border border-red-700/50">
                        <span className="text-red-300">{t('row')} {error.row}:</span>
                        <span className="text-red-200 ml-2">{error.error}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Success List */}
                {importResults.success && importResults.success.length > 0 && (
                  <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                    <h5 className="text-sm font-medium text-green-300 flex items-center">
                      <CheckCircle size={14} className="mr-1" />
                      {t('totalCreated')}:
                    </h5>
                    {importResults.success.map((item, idx) => (
                      <div key={idx} className="text-xs text-green-200">
                        {t('row')} {item.row}: {item.freediver}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={resetImportModal}
              className="border-blue-500 text-blue-300 hover:bg-blue-700/50"
            >
              {t('cancel')}
            </Button>
            {!importResults && (
              <Button
                onClick={handleImportCsv}
                disabled={!selectedCsvFile || importing}
                className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
              >
                {importing ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    {t('importing')}...
                  </>
                ) : (
                  <>
                    <FileUp className="mr-2" size={16} />
                    {t('importCSV')}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Freediver Modal */}
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) onRefresh(); }}>
        <DialogContent className="bg-gradient-to-br from-amber-900/90 to-orange-900/80 border-amber-600 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center">
              <Pencil className="mr-2" size={18} />
              {t('editFreediver')}
            </DialogTitle>
            <DialogDescription className="text-amber-300">
              {t('updateFreediverDetails')}
            </DialogDescription>
          </DialogHeader>
          {renderFreediverForm(handleUpdateFreediver, t('update'))}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.show} onOpenChange={(open) => !open && setDeleteConfirm({ show: false, freediver: null })}>
        <AlertDialogContent className="bg-gradient-to-br from-red-900/95 to-slate-900/95 border-red-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-200 flex items-center">
              <AlertTriangle className="mr-2 text-red-400" size={20} />
              {t('confirmDelete') || 'Confirm Deletion'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              {t('confirmDeleteFreediverMessage') || 'Are you sure you want to delete this freediver? This action cannot be undone.'}
              {deleteConfirm.freediver && (
                <div className="mt-3 p-3 bg-red-900/30 rounded-lg border border-red-700/50">
                  <p className="text-white font-semibold">
                    {deleteConfirm.freediver.first_name} {deleteConfirm.freediver.last_name}
                  </p>
                  <p className="text-slate-400 text-sm">{deleteConfirm.freediver.email}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600"
              disabled={deleting}
            >
              {t('cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFreediver}
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? (t('deleting') || 'Deleting...') : (t('delete') || 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FreediversTab;
